export const computeSelectedIndex = (
  scales: any[],
  selectedScaleId?: string
): number | undefined => {
  if (!scales?.length || !selectedScaleId) return undefined;
  const index = scales.findIndex(
    (s) => String(s.id) === String(selectedScaleId)
  );
  return index >= 0 ? index + 1 : undefined;
};

export const getSortedScales = (comp: any) => {
  return Array.isArray(comp?.rating_scales)
    ? [...comp.rating_scales].sort((a, b) => (a.index || 0) - (b.index || 0))
    : [];
};

export const roundToOneDecimal = (value: number): number => {
  return Number.isFinite(value) ? Number(value.toFixed(1)) : 0;
};

export const computeCompetencyAverageFilled = (
  comp: any,
  revieweeSelectedScaleId?: string,
  reviewerSelectedScaleId?: string
): number => {
  const scales = getSortedScales(comp);
  const revieweeIndex = computeSelectedIndex(scales, revieweeSelectedScaleId);
  const reviewerIndex = computeSelectedIndex(scales, reviewerSelectedScaleId);

  const values = [revieweeIndex, reviewerIndex].filter(
    (v): v is number => typeof v === 'number' && Number.isFinite(v)
  );
  if (!values.length) return 0;
  const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
  return roundToOneDecimal(avg);
};

export const computeCompetencyDisplayValue = (
  comp: any,
  isReviewerViewingOwn: boolean,
  managerSubmitted: boolean,
  revieweeSelectedScaleId?: string,
  reviewerSelectedScaleId?: string,
  selfSubmitted?: boolean
): number => {
  const scales = getSortedScales(comp);

  if (isReviewerViewingOwn) {
    // Reviewer always shows their own value
    const reviewerIndex = computeSelectedIndex(scales, reviewerSelectedScaleId);
    return reviewerIndex ? roundToOneDecimal(reviewerIndex) : 0;
  }

  // Reviewee logic: show own value by default, but manager's value only if both have submitted
  if (selfSubmitted && managerSubmitted && reviewerSelectedScaleId) {
    const reviewerIndex = computeSelectedIndex(scales, reviewerSelectedScaleId);
    return reviewerIndex ? roundToOneDecimal(reviewerIndex) : 0;
  }

  const revieweeIndex = computeSelectedIndex(scales, revieweeSelectedScaleId);
  return revieweeIndex ? roundToOneDecimal(revieweeIndex) : 0;
};

/**
 * - If decimal part is greater than 0.5, jump to the next color
 * - If exactly 0.5, stay at the lower color
 */
export const resolveDisplayIndexFromValue = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const floored = Math.floor(value);
  const fractional = value - floored;
  if (fractional > 0.5) return floored + 1;
  return floored;
};

/**
 * Picks a color from the sorted scales according to the average-filled value and the rounding rule above.
 * Falls back to the provided fallbackColor when a proper color cannot be resolved.
 */
export const resolveScaleColorForValue = (
  scales: any[],
  value: number,
  fallbackColor: string = '#2563EB'
): string => {
  if (!Array.isArray(scales) || !scales.length) return fallbackColor;
  const index = resolveDisplayIndexFromValue(value);
  if (index <= 0) return fallbackColor;
  const sorted = [...scales].sort((a, b) => (a.index || 0) - (b.index || 0));
  const item = sorted[index - 1];
  const color = item?.color;
  return typeof color === 'string' && color.trim() ? color : fallbackColor;
};
